import {
  Card,
  Box,
  Typography,
  useMediaQuery,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Skeleton,
} from "@mui/material";
import DriversStyles from "./DriversStyles";
import { IPolicyDriver } from "../../../../../models/user/policyDetails/PolicyDetails";
import CustomTable from "../../../../../common/CustomTable/CustomTable";
import { useCallback, useEffect, useState } from "react";
import AddDriverForm from "./AddDriverForm/AddDriverForm";
import { DeleteOutline, EditOutlined } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { useParams } from "react-router-dom";
import { RootState } from "../../../../../store/store";
import { postRemoveDriver } from "../../../../../store/user/policyChange/policyChangeActions";
import {
  formatDateToYYYYMMDD,
  isFeatureAvailable,
} from "../../../../../utils/Utility";
import AlertDialog from "../../../../../components/AlertDialog/AlertDialog";
import MessageDialog from "../../../../../components/MessageDialog/MessageDialog";
import CustomIcon from "../../../../../common/CustomIcon/CustomIcon";
import DriversMobileView from "./DriversMobileView/DriversMobileView";
import { useTranslation } from "react-i18next";
import CustomDrawer from "../../../../../common/CustomDrawer/CustomDrawer";
import EditDriverForm from "./EditDriverForm/EditDriverForm";

interface IProps {
  drivers: IPolicyDriver[];
  addDriver: boolean;
  displayMoreOptions: boolean;
}

function Drivers({ drivers, addDriver, displayMoreOptions }: IProps) {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { policyId, system, carrier } = useParams();

  const isMobile = useMediaQuery("(max-width:600px)");

  const { features } = useAppSelector((state: RootState) => state.features);
  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { policyDetailsHeader } = useAppSelector(
    (state: RootState) => state.policyDetails
  );
  const { isSuccess, isError, calledReducerType, isLoading } = useAppSelector(
    (state) => state.common
  );

  const [addDriverFormOpen, setAddDriverFormOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<IPolicyDriver | null>(
    null
  );
  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
  const [editDriverFormOpen, setEditDriverFormOpen] = useState<boolean>(false);

  const open = Boolean(anchorEl);

  const handleAddDriverClick = useCallback(() => {
    setAddDriverFormOpen(true);
  }, []);

  const handleMoreClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    driver: IPolicyDriver
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedDriver(driver);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditDriverClick = useCallback(() => {
    handleMenuClose();
    setEditDriverFormOpen(true);
  }, []);

  const handleDeleteDriverClick = () => {
    handleMenuClose();
    setAlertDialogOpen(true);
  };

  const handleAlertDialogCancelClick = () => {
    setAlertDialogOpen(false);
  };

  const handleAlertDialogOkClick = () => {
    setAlertDialogOpen(false);
    if (
      selectedDriver &&
      policyId &&
      userDetails &&
      userDetails.customerId &&
      policyDetailsHeader &&
      policyDetailsHeader.carrierPolicyNumber &&
      system &&
      carrier &&
      policyDetailsHeader.lobCode
    ) {
      dispatch(
        postRemoveDriver({
          carrierPolicyNo: policyDetailsHeader.carrierPolicyNumber,
          changeDt: formatDateToYYYYMMDD(new Date()),
          customerId: userDetails.customerId,
          detailDescription: "Remove driver from the policy",
          driverInfo: {
            driverAge: selectedDriver.driverAge.toString(),
            dateOfBirth: selectedDriver.dateOfBirth,
            genderCd: selectedDriver.genderCd,
            givenName: selectedDriver.givenName,
            licensedStateProvCd: selectedDriver.licensedStateProvCd,
            licenseDt: selectedDriver.licenseDt,
            licenseNumber: selectedDriver.licenseNumber,
            maritalStatus: selectedDriver.maritalStatus,
            otherGivenName: selectedDriver.otherGivenName,
            relationshipToInsuredCd: selectedDriver.relationshipToInsuredCd,
            surName: selectedDriver.surName,
          },
          policyRef: policyId,
          system,
          carrier,
          lobCode: policyDetailsHeader.lobCode
        })
      );
    }
  };

  const handleMessageModalClose = useCallback(() => {
    setMessageDialogOpen(false);
  }, []);

  const columns = [
    {
      label: t("Name"),
      Cell: (row: any) => {
        return `${row.givenName} ${row.surName}`;
      },
    },
    {
      label: t("License"),
      Cell: (row: any) => {
        return `${
          row.licensedStateProvCd ? `${row.licensedStateProvCd}-` : ""
        }${row.licenseNumber}`;
      },
    },
    {
      label: t("Type"),
      accessorKey: "typeOfDriver",
      Cell: (row: any) => {
        return row.typeOfDriver || "Principal";
      },
    },
    {
      label: t("Gender"),
      accessorKey: "genderCd",
    },
    {
      label: t("Age"),
      accessorKey: "driverAge",
    },
    {
      label: t("Marital Status"),
      accessorKey: "maritalStatus",
    },
    {
      label: "",
      accessorKey: "actions",
      Cell: (driver: any) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isFeatureAvailable({
            featureCodes: ["lob_pa_policies_edit_driver"],
            features,
          }) ? (
            <Tooltip
              title="Update Driver"
              onClick={() => {
                setSelectedDriver(driver);
                handleEditDriverClick();
              }}
            >
              <IconButton>
                <EditOutlined />
              </IconButton>
            </Tooltip>
          ) : (
            <></>
          )}
          {isFeatureAvailable({
            featureCodes: ["lob_pa_policies_delete_driver"],
            features,
          }) ? (
            <Tooltip title="Remove Driver">
              <IconButton
                disabled={drivers.length === 1}
                onClick={() => {
                  setSelectedDriver(driver);
                  handleDeleteDriverClick();
                }}
              >
                <DeleteOutline />
              </IconButton>
            </Tooltip>
          ) : (
            <></>
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "policyChange/postRemoveDriver"
    ) {
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType]);

  return (
    <>
      <Box 
        sx={{ marginTop: "20px", display: "grid", gap: "20px" }}
        id="drivers_section"
        data-test-id="drivers_section"
        aria-label="Drivers section"
      >
        <Box 
          sx={DriversStyles.headingAndButton}
          id="drivers_header"
          data-test-id="drivers_header"
          aria-label="Drivers header"
        >
          <Box 
            sx={DriversStyles.vehicleHeading}
            id="drivers_heading"
            data-test-id="drivers_heading"
            aria-label="Drivers heading"
          >
            <CustomIcon 
              iconName="Driver"
            />
            <Typography 
              sx={DriversStyles.HeadingText}
              id="drivers_title"
              data-test-id="drivers_title"
              aria-label={`Drivers (${drivers.length})`}
            >
              {`${t("Drivers")} (${drivers.length})`}
            </Typography>
          </Box>
          {isFeatureAvailable({
            featureCodes: ["lob_pa_policies_add_driver"],
            features,
          }) &&
          policyDetailsHeader?.carrierPolicyStatus !== "Cancelled" &&
          addDriver ? (
            <Button
              id="drivers_add_button"
              data-test-id="drivers_add_button"
              aria-label="Add new driver"
              variant="contained"
              sx={DriversStyles.addVehicleButton}
              onClick={handleAddDriverClick}
            >
              {t("Add Driver")}
            </Button>
          ) : (
            <></>
          )}
        </Box>
        {isMobile ? (
          isLoading && calledReducerType === "policyDetails/GetPolicyRisks" ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                paddingY: "0.5rem",
                gap: "0.8rem ",
              }}
              id="drivers_loading_skeleton"
              data-test-id="drivers_loading_skeleton"
              aria-label="Loading drivers data"
            >
              {Array.from(Array(3)).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  width="100%"
                  height={120}
                  animation="wave"
                  id={`drivers_skeleton_${index}`}
                  data-test-id={`drivers_skeleton_${index}`}
                  aria-label={`Loading driver ${index + 1}`}
                />
              ))}
            </Box>
          ) : (
            <DriversMobileView
              drivers={drivers}
              handleMoreClick={handleMoreClick}
              displayMoreOptions={displayMoreOptions}
            />
          )
        ) : (
          <CustomTable
            columns={columns.filter((column) => {
              if (column.accessorKey === "actions") {
                return (
                  isFeatureAvailable({
                    featureCodes: [
                      "lob_pa_policies_edit_driver",
                      "lob_pa_policies_delete_driver",
                    ],
                    features,
                  }) && displayMoreOptions
                );
              }
              return true;
            })}
            data={drivers}
            displaySkeleton={
              isLoading && calledReducerType === "policyDetails/GetPolicyRisks"
            }
          />
        )}
      </Box>
      <CustomDrawer
        open={addDriverFormOpen}
        onClose={() => setAddDriverFormOpen(false)}
        title={t("Add Driver")}
        content={<AddDriverForm open={true} setOpen={setAddDriverFormOpen} />}
        contentStyles={{ padding: 0 }}
      />
      <Menu 
        anchorEl={anchorEl} 
        open={open} 
        onClose={handleMenuClose}
        id="drivers_menu"
        data-test-id="drivers_menu"
        aria-label="Driver options menu"
      >
        {isFeatureAvailable({
          featureCodes: ["lob_pa_policies_edit_driver"],
          features,
        }) ? (
          <MenuItem 
            onClick={handleEditDriverClick}
            id="drivers_edit"
            data-test-id="drivers_edit"
            aria-label="Edit driver"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <EditOutlined sx={{ fontSize: "16px" }} />
              <Typography>{t("Edit")}</Typography>
            </Box>
          </MenuItem>
        ) : null}

        {isFeatureAvailable({
          featureCodes: ["lob_pa_policies_delete_driver"],
          features,
        }) ? (
          <MenuItem
            onClick={handleDeleteDriverClick}
            disabled={drivers.length === 1}
            id="drivers_remove"
            data-test-id="drivers_remove"
            aria-label="Remove driver"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <DeleteOutline sx={{ fontSize: "16px" }} />
              <Typography>{t("Remove")}</Typography>
            </Box>
          </MenuItem>
        ) : null}
      </Menu>
      <AlertDialog
        handleCancelClick={handleAlertDialogCancelClick}
        handleOkClick={handleAlertDialogOkClick}
        open={alertDialogOpen}
        title={t(
          "Removing this driver will end its coverage under this policy"
        )}
      />
      {selectedDriver ? (
        <CustomDrawer
          open={editDriverFormOpen}
          onClose={() => setEditDriverFormOpen(false)}
          title={t("Update Driver")}
          content={
            <EditDriverForm
              open={true}
              setOpen={setEditDriverFormOpen}
              driver={selectedDriver}
            />
          }
          contentStyles={{ padding: 0 }}
        />
      ) : (
        <></>
      )}
      <MessageDialog
        handleClose={handleMessageModalClose}
        open={messageDialogOpen}
      />
    </>
  );
}

export default Drivers;
